/**
 * v109.9.16 — Travel Settlement form (Local + Tour)
 * -------------------------------------------------
 * Massive multi-section form. Kept in a single file for cohesion — child
 * sections are internal components so all state lives in one place.
 *
 * Users invoke it via "File Settlement" button. On submit it POSTs to
 * /api/travel-settlements. In edit mode (after Rejection) it PATCHes.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { emitDataChange } from "../lib/events";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Calendar as CalIcon, MapPin, Users as UsersIcon, Bus, Train, Plane,
  Bike, Car, Landmark, ReceiptText, Plus, Trash2, Paperclip, LinkIcon, X,
  Building2, Utensils, ClipboardList, FileCheck2, Download, Printer, ShieldCheck,
} from "lucide-react";

const PURPOSE_TAGS = ["Sale", "Demo", "Training", "Support", "Meeting", "Other"];
const LOCAL_MODES  = ["Car", "Bike", "Auto", "Bus", "Other"];
const TOUR_LOCAL_MODES = ["Auto", "Bus", "Other"];
const INTER_MODES = ["Bus", "Train", "Flight"];
const RATE = { Car: 8, Bike: 4, Auto: 12 };

const num = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};
const daysBetween = (a, b) => {
  if (!a || !b) return 0;
  const da = new Date(a), db = new Date(b);
  if (isNaN(da) || isNaN(db) || db < da) return 0;
  return Math.max(1, Math.ceil((db - da) / 86400000));
};
const daysBetweenDT = (a, b) => {
  if (!a || !b) return 0;
  const da = new Date(a), db = new Date(b);
  if (isNaN(da) || isNaN(db) || db < da) return 0;
  return Math.max(1, Math.ceil((db - da) / 86400000));
};

function computeConveyanceAmount(c) {
  if ((c.mode === "Car" || c.mode === "Bike") && num(c.kms) > 0) return num(c.kms) * RATE[c.mode];
  if (c.mode === "Auto" && !c.has_bill && num(c.kms) > 0) return num(c.kms) * RATE.Auto;
  return num(c.amount);
}

/**
 * v109.9.17 — Print-to-PDF helper.
 * Opens a new window with a print-friendly rendering of the current form
 * (Local or Tour) and immediately triggers window.print() so the browser
 * can save it as PDF for physical record-keeping.
 *
 * v109.9.176 — Trimmed unused blocks: empty "Proof", empty "Trip-wide
 * clients", per-day meta noise, the S1 "Booked by" column, and the
 * duplicated per-day amount line in S3.
 */
function generateSettlementPDF({ tab, form, user, totals, editRecord, settlementCode = "" }) {
  const employeeName = user?.name || "";
  const employeeCode = user?.employee_code || user?.id || "";
  const status = editRecord?.status || "Draft (not yet filed)";
  const code = settlementCode || editRecord?.settlement_code || "";
  const now = new Date();
  const stamp = now.toLocaleString("en-IN", { hour12: true });
  const title = tab === "local" ? "Local Travel Settlement" : "Tour Travel Settlement";

  const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const money = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;

  // v109.9.176 — Only render meta chips that actually have a value.
  const metaChips = [];

  let body = "";
  if (tab === "local") {
    metaChips.push(`<span><b>Month:</b> ${esc(form.month)} ${esc(form.year)}</span>`);
    if (form.client_names?.length) {
      metaChips.push(`<span><b>Trip-wide clients:</b> ${esc(form.client_names.join(", "))}</span>`);
    }
    const proof = form.local_proof_link
      || (form.local_proof_file ? `Attached: ${form.local_proof_file.name}` : "")
      || (editRecord?.local_proof?.filename ? `Uploaded: ${editRecord.local_proof.filename}` : "");
    if (proof) metaChips.push(`<span><b>Proof:</b> ${esc(proof)}</span>`);
    body += `<div class="meta">${metaChips.join("")}</div>`;
    for (const [i, e] of form.local_entries.entries()) {
      let dayTotal = 0;
      const convRows = e.conveyances.map((c) => {
        const amt = computeConveyanceAmount(c); dayTotal += amt;
        return `<tr>
          <td>${esc(c.mode)}${c.mode_other ? ` (${esc(c.mode_other)})` : ""}</td>
          <td class="r">${c.kms || "—"}</td>
          <td class="r">${money(amt)}</td>
          <td>${c.has_bill ? "Yes" : (c.no_bill_reason ? `No — ${esc(c.no_bill_reason)}` : "No")}</td>
        </tr>`;
      }).join("");
      body += `
        <div class="entry">
          <div class="entry-head">Entry #${i + 1} · ${esc(e.date)} · ${esc(e.start_location)} → ${esc(e.end_location)}</div>
          ${e.reason ? `<div class="muted">Reason: ${esc(e.reason)}</div>` : ""}
          ${e.client_names?.length ? `<div class="muted">Clients met: ${esc(e.client_names.join(", "))}</div>` : ""}
          <table>
            <thead><tr><th>Mode</th><th class="r">KMs</th><th class="r">Amount</th><th>Bill?</th></tr></thead>
            <tbody>${convRows}</tbody>
          </table>
          <div class="entry-total">Day total: <b>${money(dayTotal)}</b></div>
        </div>`;
    }
    // Local misc items block — only when reasons exist do we render a Reason column.
    if ((form.local_misc_items || []).length) {
      const hasReason = form.local_misc_items.some((m) => (m.reason || "").trim());
      body += `<h3>Miscellaneous Expenses</h3><table>
        <thead><tr><th>Date</th><th>Type</th>${hasReason ? "<th>Reason</th>" : ""}<th class="r">Amount</th><th>Bill?</th></tr></thead>
        <tbody>${form.local_misc_items.map((m) => `<tr>
          <td>${esc(m.date)}</td>
          <td>${esc(m.expense_type)}</td>
          ${hasReason ? `<td>${esc(m.reason || "")}</td>` : ""}
          <td class="r">${money(m.amount)}</td>
          <td>${m.has_bill ? "Yes" : (m.no_bill_reason ? `No — ${esc(m.no_bill_reason)}` : "No")}</td>
        </tr>`).join("")}</tbody></table>
        <div class="section-total">Misc total: <b>${money(form.local_misc_items.reduce((s, m) => s + num(m.amount), 0))}</b></div>`;
    }
  } else {
    metaChips.push(`<span><b>Purpose:</b> ${esc(form.purpose_of_travel)}</span>`);
    if (form.assigned_by_name) metaChips.push(`<span><b>Assigned by:</b> ${esc(form.assigned_by_name)}</span>`);
    if (form.client_names?.length) metaChips.push(`<span><b>Clients:</b> ${esc(form.client_names.join(", "))}</span>`);
    body += `<div class="meta">${metaChips.join("")}</div>`;
    const en = form.sections_enabled;
    if (en.s1 && form.s1_lines?.length) {
      body += `<h3>Section 1 · Inter-State Travel</h3><table>
        <thead><tr><th>Departure</th><th>Arrival</th><th>Mode/Class</th><th class="r">Amount</th><th>Bill?</th></tr></thead>
        <tbody>${form.s1_lines.map((l) => `<tr>
          <td>${esc(l.departure_date)} ${esc(l.departure_time)}<br>${esc(l.departure_location)}</td>
          <td>${esc(l.arrival_date)} ${esc(l.arrival_time)}<br>${esc(l.arrival_location)}</td>
          <td>${esc(l.mode)} · ${esc(l.travel_class)}${l.tatkal ? " · Tatkal" : ""}</td>
          <td class="r">${money(l.amount)}</td>
          <td>${l.has_bill ? "Yes" : "No — " + esc(l.no_bill_reason)}</td>
        </tr>`).join("")}</tbody></table>
        <div class="section-total">Section 1 total: <b>${money(totals.s1)}</b></div>`;
    }
    if (en.s2 && form.s2_entries?.length) {
      body += `<h3>Section 2 · Local Conveyance</h3>`;
      for (const [i, e] of form.s2_entries.entries()) {
        let dayTotal = 0;
        const rows = e.conveyances.map((c) => {
          const amt = computeConveyanceAmount(c); dayTotal += amt;
          return `<tr><td>${esc(c.mode)}</td><td class="r">${c.kms || "—"}</td><td class="r">${money(amt)}</td><td>${c.has_bill ? "Yes" : "No — " + esc(c.no_bill_reason || "")}</td></tr>`;
        }).join("");
        body += `<div class="entry"><div class="entry-head">Day #${i + 1} · ${esc(e.date)} · ${esc(e.start_location)} → ${esc(e.end_location)}</div>
          <table><thead><tr><th>Mode</th><th class="r">KMs</th><th class="r">Amount</th><th>Bill?</th></tr></thead><tbody>${rows}</tbody></table>
          <div class="entry-total">Day total: <b>${money(dayTotal)}</b></div></div>`;
      }
      body += `<div class="section-total">Section 2 total: <b>${money(totals.s2)}</b></div>`;
    }
    if (en.s3) {
      const s3 = form.s3;
      const days = s3.days || daysBetweenDT(s3.start_datetime, s3.end_datetime);
      body += `<h3>Section 3 · DA / Fooding</h3>
        <div class="pair">Days: <b>${days}</b> · Per-day: <b>${money(s3.per_day_amount)}</b></div>
        <div class="section-total">Section 3 total: <b>${money(totals.s3)}</b></div>`;
    }
    if (en.s4 && form.s4_hotels?.length) {
      body += `<h3>Section 4 · Lodging</h3><table>
        <thead><tr><th>Hotel</th><th>Location</th><th>From</th><th>To</th><th class="r">Days</th><th class="r">Rate</th><th class="r">Total</th></tr></thead>
        <tbody>${form.s4_hotels.map((h) => {
          const d = h.days || (h.from_date && h.to_date ? Math.max(1, Math.ceil((new Date(h.to_date) - new Date(h.from_date)) / 86400000) + 1) : 0);
          return `<tr><td>${esc(h.hotel_name)}</td><td>${esc(h.location)}</td><td>${esc(h.from_date)}</td><td>${esc(h.to_date)}</td><td class="r">${d}</td><td class="r">${money(h.amount_per_day)}</td><td class="r">${money(d * num(h.amount_per_day))}</td></tr>`;
        }).join("")}</tbody></table>
        <div class="section-total">Section 4 total: <b>${money(totals.s4)}</b></div>`;
    }
    if (en.s5 && form.s5_items?.length) {
      const hasReason = form.s5_items.some((m) => (m.reason || "").trim());
      body += `<h3>Section 5 · Miscellaneous</h3><table>
        <thead><tr><th>Date</th><th>Type</th>${hasReason ? "<th>Reason</th>" : ""}<th class="r">Amount</th><th>Bill?</th></tr></thead>
        <tbody>${form.s5_items.map((m) => `<tr>
          <td>${esc(m.date)}</td>
          <td>${esc(m.expense_type)}</td>
          ${hasReason ? `<td>${esc(m.reason || "")}</td>` : ""}
          <td class="r">${money(m.amount)}</td>
          <td>${m.has_bill ? "Yes" : "No — " + esc(m.no_bill_reason)}</td>
        </tr>`).join("")}</tbody></table>
        <div class="section-total">Section 5 total: <b>${money(totals.s5)}</b></div>`;
    }
  }

  const html = `<!doctype html><html><head><meta charset="utf-8">
    <title>${title} — ${esc(employeeName)}</title>
    <style>
      * { box-sizing: border-box; }
      body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #1e293b; margin: 24px; }
      .hdr { border-bottom: 2px solid #4f46e5; padding-bottom: 8px; margin-bottom: 12px; }
      .hdr h1 { margin: 0 0 4px 0; color: #4338ca; font-size: 20px; }
      .hdr .who { font-size: 12px; color: #475569; display: flex; gap: 12px; flex-wrap: wrap; }
      .who b { color: #0f172a; }
      .status { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; background: #fef3c7; color: #92400e; margin-left: 6px; }
      .meta { display: flex; gap: 12px; flex-wrap: wrap; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px; font-size: 12px; margin: 10px 0 14px 0; }
      h3 { font-size: 13px; color: #0f172a; margin: 14px 0 6px 0; border-left: 3px solid #4f46e5; padding-left: 8px; }
      table { width: 100%; border-collapse: collapse; font-size: 11px; }
      th, td { border: 1px solid #cbd5e1; padding: 4px 6px; text-align: left; vertical-align: top; }
      th { background: #eef2ff; color: #3730a3; font-weight: 600; }
      td.r, th.r { text-align: right; font-variant-numeric: tabular-nums; }
      .muted { font-size: 11px; color: #64748b; margin: 3px 0; }
      .entry { border: 1px solid #cbd5e1; border-radius: 6px; padding: 8px; margin: 8px 0; }
      .entry-head { font-weight: 600; font-size: 12px; margin-bottom: 4px; color: #1e293b; }
      .entry-total { text-align: right; font-size: 11.5px; color: #4338ca; margin-top: 4px; }
      .section-total { text-align: right; font-size: 12.5px; color: #4338ca; margin: 4px 0 14px 0; }
      .grand { border: 2px solid #4f46e5; background: #eef2ff; padding: 8px 12px; margin-top: 14px; display: flex; justify-content: space-between; align-items: center; border-radius: 6px; }
      .grand .lbl { font-size: 13px; font-weight: 600; }
      .grand .amt { font-size: 18px; font-weight: 700; color: #4338ca; }
      .footer { margin-top: 22px; padding-top: 10px; border-top: 1px dashed #cbd5e1; font-size: 10.5px; color: #64748b; display: flex; justify-content: space-between; }
      .pair { font-size: 12px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 6px 8px; }
      @media print {
        body { margin: 12mm; }
        .no-print { display: none; }
      }
    </style></head><body>
    <div class="hdr">
      <div class="who">
        <span><b>Employee:</b> ${esc(employeeName)}</span>
        <span><b>Employee ID:</b> ${esc(employeeCode)}</span>
        <span><b>Status:</b> <span class="status">${esc(status)}</span></span>
      </div>
      <h1>${title}</h1>
      ${code ? `<div style="margin-top:6px;padding:6px 10px;background:#4338ca;color:#fff;font-weight:700;letter-spacing:0.08em;font-size:14px;border-radius:4px;display:inline-block;">Settlement Code: ${esc(code)}</div>` : ""}
    </div>
    ${body}
    <div class="grand"><div class="lbl">Claim Amount</div><div class="amt">${money(totals.grand)}</div></div>
    <div class="footer">
      <div>Downloaded on <b>${esc(stamp)}</b></div>
      <div>Panchakosh Ventures · Travel Settlement · v109.9.17</div>
    </div>
    <script>window.onload=()=>{setTimeout(()=>window.print(),250);};</script>
    </body></html>`;

  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) {
    alert("Please allow pop-ups to download the settlement PDF");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}


const blankConv = () => ({
  line_id: "",
  mode: "",
  mode_other: "",
  kms: "",
  amount: "",
  has_bill: false,
  no_bill_reason: "",
  bill_link: "",
});
const blankLocalEntry = () => ({
  entry_id: "",
  date: "",
  start_location: "",
  end_location: "",
  client_ids: [],
  client_names: [],
  reason: "",
  conveyances: [blankConv()],
  day_total: 0,
});
const blankS1Line = () => ({
  line_id: "",
  departure_date: "", departure_time: "", departure_location: "",
  arrival_date: "", arrival_time: "", arrival_location: "",
  mode: "", travel_class: "", tatkal: false,
  amount: "", booked_by: "self", booked_by_user_id: "", booked_by_user_name: "",
  remark: "", has_bill: false, no_bill_reason: "",
  bill_link: "",
});
const blankHotel = () => ({
  line_id: "", from_date: "", to_date: "", days: 0,
  hotel_name: "", location: "", amount_per_day: "", total: 0,
  has_bill: false, no_bill_reason: "", bill_link: "",
});
const blankMisc = () => ({
  line_id: "", date: "", expense_type: "", reason: "",
  amount: "", remark: "", has_bill: false, no_bill_reason: "", bill_link: "",
});

export default function TravelSettlementFormDialog({ open, onClose, onSaved, editRecord = null }) {
  const { user } = useAuth();
  const isEdit = !!editRecord;
  // v109.9.178 — Accounts (or Master) editing a submitted settlement from
  // the Accounts Tab. Their save flows straight back to the employee's row.
  const isAccountsUser = user?.role === "master_admin" || user?.role === "accounts_admin"
    || (user?.granted_modules_edit || []).some((m) => ["accounts_advances", "accounts_claims", "accounts_invoices"].includes(m));
  const editingPastDraft = isEdit && !["Draft", "Downloaded", "Rejected"].includes(editRecord?.status || "");
  const isAccountsEdit = editingPastDraft && isAccountsUser;

  const [tab, setTab] = useState("local"); // local | tour
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(freshForm());
  // v109.9.106 — Draft lifecycle: once created, the settlement id is
  // remembered so subsequent auto-saves PATCH instead of POST.
  const [draftId, setDraftId] = useState(null);
  const [draftStatus, setDraftStatus] = useState("");
  const [savedRecord, setSavedRecord] = useState(null); // last-saved doc for code + status
  const [instructions, setInstructions] = useState(null); // banner payload
  const [showInstructions, setShowInstructions] = useState(false);

  function freshForm() {
    return {
      // shared
      client_ids: [],
      client_names: [],
      // local
      month: "", year: String(new Date().getFullYear()),
      local_entries: [blankLocalEntry()],
      local_proof_link: "",
      local_proof_file: null,
      local_misc_items: [],
      // tour
      purpose_of_travel: "",
      assigned_by_id: "", assigned_by_name: "",
      sections_enabled: { s1: true, s2: true, s3: true, s4: true, s5: true, local_misc: true },
      s1_lines: [blankS1Line()],
      s2_entries: [blankLocalEntry()],
      s3: { start_datetime: "", end_datetime: "", days: 0, per_day_amount: "", total: 0 },
      s4_hotels: [blankHotel()],
      s5_items: [blankMisc()],
    };
  }

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        // v109.9.165 — Eligible-clients scoping (SA + trainer-assigned).
        const [{ data: c }, { data: cAll }, { data: u }] = await Promise.all([
          api.get("/travel/eligible-clients").catch(() => ({ data: [] })),
          api.get("/records/crm_clients").catch(() => ({ data: [] })),
          api.get("/directory").catch(() => ({ data: [] })),
        ]);
        const list = Array.isArray(c) && c.length > 0 ? c : (Array.isArray(cAll) ? cAll : []);
        setClients(list);
        setUsers(Array.isArray(u) ? u : (u?.users || []));
      } catch { /* ignore */ }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (editRecord) {
      setTab(editRecord.kind || "local");
      setDraftId(editRecord.id || null);
      setSavedRecord(editRecord);
      setDraftStatus(editRecord.status === "Draft" ? "Draft loaded" : "");
      setForm({
        client_ids: editRecord.client_ids || [],
        client_names: editRecord.client_names || [],
        month: editRecord.month || "",
        year: editRecord.year || String(new Date().getFullYear()),
        local_entries: editRecord.local_entries?.length ? editRecord.local_entries : [blankLocalEntry()],
        local_proof_link: editRecord.local_proof?.link || "",
        local_proof_file: null,
        local_misc_items: editRecord.local_misc_items || [],
        purpose_of_travel: editRecord.purpose_of_travel || "",
        assigned_by_id: editRecord.assigned_by_id || "",
        assigned_by_name: editRecord.assigned_by_name || "",
        sections_enabled: editRecord.sections_enabled || { s1: true, s2: true, s3: true, s4: true, s5: true },
        s1_lines: editRecord.s1_lines?.length ? editRecord.s1_lines : [blankS1Line()],
        s2_entries: editRecord.s2_entries?.length ? editRecord.s2_entries : [blankLocalEntry()],
        s3: editRecord.s3 || { start_datetime: "", end_datetime: "", days: 0, per_day_amount: "", total: 0 },
        s4_hotels: editRecord.s4_hotels?.length ? editRecord.s4_hotels : [blankHotel()],
        s5_items: editRecord.s5_items?.length ? editRecord.s5_items : [blankMisc()],
      });
    } else {
      setForm(freshForm());
      setTab("local");
      setDraftId(null);
      setSavedRecord(null);
      setDraftStatus("");
    }
  }, [open, editRecord]);

  // Derived totals for live preview
  const totals = useMemo(() => {
    if (tab === "local") {
      let g = 0;
      for (const e of form.local_entries) for (const c of e.conveyances) g += computeConveyanceAmount(c);
      // v109.9.167 — Miscellaneous Expenses now roll into the Local total.
      // Previously misc amounts were captured but never summed, which caused
      // the Claim Amount to under-report by the misc total. Now included.
      const miscTotal = (form.local_misc_items || []).reduce((s, m) => s + num(m.amount), 0);
      return { grand: g + miscTotal, s1: 0, s2: 0, s3: 0, s4: 0, s5: 0, local_misc: miscTotal };
    }
    const en = form.sections_enabled;
    let s1 = 0, s2 = 0, s3 = 0, s4 = 0, s5 = 0;
    if (en.s1) for (const l of form.s1_lines) s1 += num(l.amount);
    if (en.s2) for (const e of form.s2_entries) for (const c of e.conveyances) s2 += computeConveyanceAmount(c);
    if (en.s3) {
      const d = form.s3.days || daysBetweenDT(form.s3.start_datetime, form.s3.end_datetime);
      s3 = d * num(form.s3.per_day_amount);
    }
    if (en.s4) for (const h of form.s4_hotels) s4 += (h.days || daysBetween(h.from_date, h.to_date)) * num(h.amount_per_day);
    if (en.s5) for (const m of form.s5_items) s5 += num(m.amount);
    return { grand: s1 + s2 + s3 + s4 + s5, s1, s2, s3, s4, s5 };
  }, [tab, form]);

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  /* v109.9.106 — Build the payload once so both auto-save and manual-save
     paths stay in sync. */
  const buildPayload = () => (
    tab === "local"
      ? {
          kind: "local",
          month: form.month, year: form.year,
          client_ids: form.client_ids, client_names: form.client_names,
          local_entries: form.local_entries,
          local_misc_items: form.local_misc_items || [],
          local_proof: {},
        }
      : {
          kind: "tour",
          purpose_of_travel: form.purpose_of_travel,
          assigned_by_id: form.assigned_by_id, assigned_by_name: form.assigned_by_name,
          client_ids: form.client_ids, client_names: form.client_names,
          sections_enabled: form.sections_enabled,
          s1_lines: form.s1_lines,
          s2_entries: form.s2_entries,
          s3: form.s3,
          s4_hotels: form.s4_hotels,
          s5_items: form.s5_items,
        }
  );

  /* Silent auto-save. On the very first call (no draftId yet) we
     require the bare minimum (tour: purpose, local: month) so we don't
     POST a completely empty document on the first keystroke. */
  const autoSaveDraft = async () => {
    // v109.9.178 — Draft-only auto-save. Accounts users editing a
    // post-Draft row use the explicit "Save Changes" button below.
    if (savedRecord && !["Draft", "Downloaded", "Rejected"].includes(savedRecord.status)) return;
    if (isAccountsEdit) return;
    if (tab === "tour" && !form.purpose_of_travel && !draftId) return;
    if (tab === "local" && !form.month && !draftId) return;
    try {
      let saved;
      if (draftId) {
        const r = await api.patch(`/travel-settlements/${draftId}`, buildPayload());
        saved = r.data;
      } else {
        const r = await api.post("/travel-settlements", buildPayload());
        saved = r.data;
        setDraftId(saved.id);
      }
      setSavedRecord(saved);
      const stamp = new Date().toLocaleTimeString("en-IN", { hour12: true });
      setDraftStatus(`Draft auto-saved · ${stamp} · ${saved.settlement_code || ""}`);
    } catch (e) {
      setDraftStatus(`Auto-save failed: ${formatError(e.response?.data?.detail)}`);
    }
  };

  /* v109.9.106 — Manual "Save Draft" click. Same as auto-save but with
     user-visible toasts + no early-exit for tiny forms. */
  const saveAsDraft = async () => {
    if (savedRecord && !["Draft", "Downloaded", "Rejected"].includes(savedRecord.status)) {
      toast.info("This settlement is already submitted — cannot save as draft.");
      return;
    }
    setSaving(true);
    try {
      let saved;
      if (draftId) {
        const r = await api.patch(`/travel-settlements/${draftId}`, buildPayload());
        saved = r.data;
      } else {
        const r = await api.post("/travel-settlements", buildPayload());
        saved = r.data;
        setDraftId(saved.id);
      }
      setSavedRecord(saved);
      const stamp = new Date().toLocaleTimeString("en-IN", { hour12: true });
      setDraftStatus(`Draft saved · ${stamp} · ${saved.settlement_code || ""}`);
      toast.success(`Draft saved · ${saved.settlement_code}`);
      onSaved?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  /* v109.9.178 — Accounts / Master Admin editing a POST-Draft settlement
     from the Accounts Tab. Explicit "Save Changes" — no auto-save, so
     Accounts stays in control of exactly what lands back on the row. The
     backend PATCH updates the shared row in-place, so the employee sees
     the new numbers the next time they open the tab. */
  const saveAccountsEdit = async () => {
    if (!editRecord?.id) return;
    setSaving(true);
    try {
      const r = await api.patch(`/travel-settlements/${editRecord.id}`, buildPayload());
      setSavedRecord(r.data);
      // v109.9.179 — Broadcast so dashboards, approval tab, employee's own
      // settlement tab & unsettled summary all refresh on their next tick.
      emitDataChange("travel:updated", { kind: "settlement", id: editRecord.id, record: r.data });
      emitDataChange("travel:settlement:updated", { id: editRecord.id, record: r.data });
      toast.success("Changes saved — employee will see the update.");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  /* Auto-save-as-draft with 2s debounce on every form change. Turned off
     when the settlement has moved past Draft/Downloaded. */
  useEffect(() => {
    if (!open) return;
    if (savedRecord && !["Draft", "Downloaded", "Rejected"].includes(savedRecord.status)) return;
    if (isAccountsEdit) return;
    // Only kick in once the user has entered something meaningful.
    const hasContent = tab === "local"
      ? (form.month || form.local_entries.some((e) => e.date || e.start_location))
      : (form.purpose_of_travel || form.assigned_by_id);
    if (!hasContent && !draftId) return;
    const t = setTimeout(() => { autoSaveDraft(); }, 2000);
    return () => clearTimeout(t);
  }, [form, tab, open]);

  /* Basic validation shared by "Download" so we don't ship broken PDFs. */
  const validate = () => {
    if (tab === "local") {
      if (!form.month || !form.year) { toast.error("Month + Year are required"); return false; }
      if (form.local_entries.length === 0) { toast.error("Add at least one date entry"); return false; }
      for (const e of form.local_entries) {
        if (!e.date || !e.start_location || !e.end_location) { toast.error("Every entry needs Date + Start + End Location"); return false; }
        for (const c of e.conveyances) {
          if (!c.mode) { toast.error("Select a mode for every conveyance line"); return false; }
          if (c.mode === "Other" && !c.mode_other) { toast.error("Describe the 'Other' mode"); return false; }
          // v109.9.163 — Car & Bike are ALWAYS mileage-based (₹8/km, ₹4/km);
          // there is no invoice/receipt to attach, so the "reason for no
          // proof" check must not apply. Auto also skips the check when
          // the user chose the per-km path (has_bill=false → distance mode).
          const isDistanceMode = c.mode === "Car" || c.mode === "Bike" || (c.mode === "Auto" && !c.has_bill);
          if (!c.has_bill && !c.no_bill_reason && !isDistanceMode) {
            toast.error("Provide a reason when no Invoice/Proof is available"); return false;
          }
        }
      }
    } else {
      if (!form.purpose_of_travel) { toast.error("Purpose of Travel is required"); return false; }
      if (!form.assigned_by_id) { toast.error("Assigned By is required"); return false; }
    }
    return true;
  };

  /* v109.9.106 — Download-as-PDF flow:
     1. Validate + save the draft so we always have a settlement code.
     2. Fetch the instructions banner (TO/CC/subject/proof list).
     3. Render the banner. On "Continue → Print" we call the printer and
        POST /mark-downloaded so the row transitions to "Downloaded". */
  const downloadForm = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      let currentId = draftId;
      let currentRec = savedRecord;
      if (!currentId) {
        const r = await api.post("/travel-settlements", buildPayload());
        currentRec = r.data;
        currentId = currentRec.id;
        setDraftId(currentId);
        setSavedRecord(currentRec);
      } else {
        const r = await api.patch(`/travel-settlements/${currentId}`, buildPayload());
        currentRec = r.data;
        setSavedRecord(currentRec);
      }
      const { data: inst } = await api.get(`/travel-settlements/${currentId}/instructions`);
      setInstructions(inst);
      setShowInstructions(true);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const confirmDownload = async () => {
    try {
      generateSettlementPDF({
        tab,
        form,
        user,
        totals,
        editRecord: savedRecord || editRecord,
        settlementCode: instructions?.settlement_code || savedRecord?.settlement_code || "",
      });
      // Mark downloaded on the backend (best-effort; ignore error so print isn't blocked).
      try {
        const { data } = await api.post(`/travel-settlements/${draftId}/mark-downloaded`);
        setSavedRecord(data);
      } catch { /* ignore */ }
      setShowInstructions(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-5xl max-h-[94vh] overflow-y-auto" data-testid="ts-form">
        <DialogHeader>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <DialogTitle className="heading">{isEdit ? "Edit Advance Settlement" : "File Advance Settlement"}</DialogTitle>
              <p className="text-[12px] text-slate-500">
                Is this a <b>Local Travel</b> (in the city, same-day return) or a <b>Tour</b> (multi-day, out-of-city)?
              </p>
              {draftStatus && (
                <div className="text-[10.5px] text-emerald-700 dark:text-emerald-400 mt-1 flex items-center gap-1" data-testid="ts-draft-status">
                  <FileCheck2 size={10} /> {draftStatus}
                </div>
              )}
              {savedRecord?.settlement_code && (
                <div className="text-[10.5px] text-indigo-700 dark:text-indigo-300 mt-1 mono" data-testid="ts-settlement-code">
                  Code: <b>{savedRecord.settlement_code}</b>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => onClose?.()}
              className="shrink-0 text-slate-400 hover:text-rose-600 rounded p-1 hover:bg-slate-100 dark:hover:bg-slate-800"
              aria-label="Close"
              data-testid="ts-close"
            >
              <X size={16} />
            </button>
          </div>
        </DialogHeader>

        {/* v109.9.167 — Locked audit banner shown when editing an existing
            settlement. Employee identity, settlement code and approval flow
            are audit-locked (backend enforces immutability too). MA can
            change the trip/cost data but not the identity fields. */}
        {isEdit && editRecord && (
          <div className="rounded-lg border-2 border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20 p-3 mt-1" data-testid="ts-locked-audit">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-amber-900 dark:text-amber-200 mb-2">
              <ShieldCheck size={12} /> Locked Audit Fields (read-only)
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[12px]">
              <div><span className="text-slate-500">Employee Name:</span> <b className="text-slate-800 dark:text-slate-100">{editRecord.created_by_name || "—"}</b></div>
              <div><span className="text-slate-500">Employee Code:</span> <b className="mono text-slate-800 dark:text-slate-100">{editRecord.employee_code || "—"}</b></div>
              <div><span className="text-slate-500">Settlement Code:</span> <b className="mono text-indigo-700 dark:text-indigo-300">{editRecord.settlement_code || "—"}</b></div>
              <div className="truncate"><span className="text-slate-500">Approval Flow:</span> <b className="text-slate-800 dark:text-slate-100">{(editRecord.approvals || []).map((a) => a.name || a.role).filter(Boolean).join(" → ") || "—"}</b></div>
            </div>
          </div>
        )}

        {/* Kind switch */}
        {!isEdit && (
          <div className="flex gap-2 mb-2" data-testid="ts-kind">
            {[
              { k: "local", label: "Local Travel", d: "Same-city, daily return" },
              { k: "tour",  label: "Tour",         d: "Multi-day out-of-city trip" },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                className={`flex-1 rounded-lg border p-3 text-left transition-all ${
                  tab === t.k
                    ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-950/40 ring-1 ring-indigo-300"
                    : "border-slate-200 dark:border-slate-700 hover:border-indigo-300"
                }`}
                data-testid={`ts-kind-${t.k}`}
              >
                <div className="text-[13px] font-semibold">{t.label}</div>
                <div className="text-[11px] text-slate-500">{t.d}</div>
              </button>
            ))}
          </div>
        )}
        {isEdit && <div className="text-[12px] text-slate-500">Kind: <b>{editRecord?.kind}</b></div>}

        <div className="space-y-5 pt-2">
          {tab === "local" ? (
            <LocalSection
              form={form}
              upd={upd}
              clients={clients}
              editRecord={editRecord}
              isEdit={isEdit}
            />
          ) : (
            <TourSection
              form={form}
              upd={upd}
              clients={clients}
              users={users}
              totals={totals}
            />
          )}

          {/* Grand total */}
          <div className="rounded-lg border-2 border-indigo-300 bg-indigo-50/60 dark:bg-indigo-950/30 p-3 flex justify-between items-center">
            <div className="text-[13px] font-semibold text-slate-800 dark:text-slate-200">Claim Amount</div>
            <div className="text-2xl font-bold text-indigo-800 dark:text-indigo-300" data-testid="ts-grand">
              ₹ {totals.grand.toLocaleString("en-IN")}
            </div>
          </div>

          {/* Approval preview */}
          <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20 p-3 text-[12px]">
            <div className="font-semibold text-emerald-900 dark:text-emerald-200 mb-1">Approval Flow</div>
            {tab === "tour" ? (
              <div className="flex flex-wrap gap-2 items-center">
                <Badge variant="outline">1. {form.assigned_by_name || "Assigned-By User"}</Badge>
                <span>→</span>
                <Badge variant="outline">2. {user?.reporting_manager_name || "Reporting Manager"}</Badge>
                <span>→</span>
                <Badge variant="outline">3. Account Team (voucher)</Badge>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 items-center">
                <Badge variant="outline">1. {user?.reporting_manager_name || "Reporting Manager"}</Badge>
                <span>→</span>
                <Badge variant="outline">2. Account Team (voucher)</Badge>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onClose?.()} disabled={saving}>Close</Button>
          {isAccountsEdit ? (
            <Button
              type="button"
              onClick={saveAccountsEdit}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="ts-accounts-save"
            >
              <FileCheck2 size={13} className="mr-1.5" /> {saving ? "Saving…" : "Save Changes"}
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={saveAsDraft}
                disabled={saving}
                className="text-emerald-700 border-emerald-300 dark:border-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                data-testid="ts-save-draft"
              >
                <FileCheck2 size={13} className="mr-1.5" /> Save Draft
              </Button>
              <Button
                type="button"
                onClick={downloadForm}
                disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="ts-download-pdf"
              >
                <Download size={13} className="mr-1.5" /> Download Form
              </Button>
            </>
          )}
        </DialogFooter>
        {showInstructions && instructions && (
          <InstructionsBanner
            instructions={instructions}
            onCancel={() => setShowInstructions(false)}
            onConfirm={confirmDownload}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/* =========================================================================
 * LOCAL section
 * =======================================================================*/
function LocalSection({ form, upd, clients, editRecord, isEdit }) {
  // v109.9.18 — Constrain each entry's date to the selected Month + Year.
  const monthIdx = ["January","February","March","April","May","June","July","August","September","October","November","December"].indexOf(form.month);
  const { minDate, maxDate } = (() => {
    const y = parseInt(form.year, 10);
    if (monthIdx < 0 || !Number.isFinite(y)) return { minDate: "", maxDate: "" };
    const pad = (n) => String(n).padStart(2, "0");
    const first = `${y}-${pad(monthIdx + 1)}-01`;
    const lastDay = new Date(y, monthIdx + 1, 0).getDate();
    const last = `${y}-${pad(monthIdx + 1)}-${pad(lastDay)}`;
    return { minDate: first, maxDate: last };
  })();

  const setEntry = (idx, patch) => {
    const arr = form.local_entries.map((e, i) => (i === idx ? { ...e, ...patch } : e));
    upd({ local_entries: arr });
  };
  const setConv = (ei, ci, patch) => {
    const arr = form.local_entries.map((e, i) => {
      if (i !== ei) return e;
      const conv = e.conveyances.map((c, j) => (j === ci ? { ...c, ...patch } : c));
      return { ...e, conveyances: conv };
    });
    upd({ local_entries: arr });
  };
  const addEntry = () => upd({ local_entries: [...form.local_entries, blankLocalEntry()] });
  const rmEntry = (i) => upd({ local_entries: form.local_entries.filter((_, x) => x !== i) });
  const addConv = (ei) => setEntry(ei, { conveyances: [...form.local_entries[ei].conveyances, blankConv()] });
  const rmConv = (ei, ci) => setEntry(ei, { conveyances: form.local_entries[ei].conveyances.filter((_, x) => x !== ci) });

  return (
    <div className="space-y-4">
      {/* Month + year + consolidated proof */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <SelectField
          label="Month *"
          value={form.month}
          onChange={(v) => upd({ month: v })}
          options={["January","February","March","April","May","June","July","August","September","October","November","December"]}
          testid="local-month"
        />
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Year *</Label>
          <Input type="number" value={form.year} onChange={(e) => upd({ year: e.target.value })} className="mt-1.5" data-testid="local-year" />
        </div>
        <div className="md:col-span-2">
          {/* v109.9.105 — Consolidated Proof upload removed. Per-line
              "Invoice/Ticket/Payment Proof" toggles now capture every
              proof reason inline; a single blob upload is no longer
              needed. */}
        </div>
      </div>

      <div className="space-y-3">
        {form.local_entries.map((e, ei) => (
          <div key={ei} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/40 p-3 space-y-3" data-testid={`local-entry-${ei}`}>
            <div className="flex items-center justify-between">
              <div className="text-[12px] font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
                <CalIcon size={13} /> Entry #{ei + 1}
              </div>
              {form.local_entries.length > 1 && (
                <Button size="sm" variant="outline" className="h-7 text-[11px] text-rose-600" onClick={() => rmEntry(ei)}>
                  <Trash2 size={11} /> Remove
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <Label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-600">Date *</Label>
                <Input
                  type="date"
                  value={e.date}
                  min={minDate || undefined}
                  max={maxDate || undefined}
                  onChange={(x) => setEntry(ei, { date: x.target.value })}
                  className="mt-1.5"
                />
                {minDate && (
                  <div className="text-[9.5px] text-slate-500 mt-0.5">Must be within {form.month} {form.year}</div>
                )}
              </div>
              <div>
                <Label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-600">Start *</Label>
                <Input value={e.start_location} onChange={(x) => setEntry(ei, { start_location: x.target.value })} className="mt-1.5" placeholder="e.g. Office" />
              </div>
              <div>
                <Label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-600">End *</Label>
                <Input value={e.end_location} onChange={(x) => setEntry(ei, { end_location: x.target.value })} className="mt-1.5" placeholder="e.g. School X" />
              </div>
            </div>

            {/* Per-entry clients met */}
            <ClientMultiPicker
              clients={clients}
              valueIds={e.client_ids || []}
              onChange={(ids, names) => setEntry(ei, { client_ids: ids, client_names: names })}
              compact
            />

            <div>
              <Label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-600">Reason for travel</Label>
              <Input value={e.reason} onChange={(x) => setEntry(ei, { reason: x.target.value })} className="mt-1.5" placeholder="Purpose of this visit" />
            </div>

            {/* Conveyances */}
            <div className="space-y-2">
              <div className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">Conveyance mode(s) — select all that apply</div>
              {e.conveyances.map((c, ci) => (
                <ConveyanceRow
                  key={ci}
                  c={c}
                  modes={LOCAL_MODES}
                  onChange={(p) => setConv(ei, ci, p)}
                  onRemove={() => rmConv(ei, ci)}
                  canRemove={e.conveyances.length > 1}
                />
              ))}
              <Button size="sm" variant="outline" onClick={() => addConv(ei)} className="h-7 text-[11px]">
                <Plus size={11} className="mr-1" /> Add another conveyance
              </Button>
              <div className="text-[11px] text-slate-500">
                Day total: <b>₹ {e.conveyances.reduce((s, c) => s + computeConveyanceAmount(c), 0).toLocaleString("en-IN")}</b>
              </div>
            </div>
          </div>
        ))}
        <Button variant="outline" onClick={addEntry} className="text-[11.5px]" data-testid="local-add-date">
          <Plus size={12} className="mr-1.5" /> Add another Date
        </Button>
      </div>

      {/* v109.9.18 — Section 5-style Miscellaneous Expenses for Local Travel */}
      <div className="rounded-lg border-2 border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-950/20 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[12.5px] font-semibold text-amber-900 dark:text-amber-200 flex items-center gap-1.5">
            <ClipboardList size={14} /> Miscellaneous Expenses (optional)
          </div>
          <div className="text-[11px] text-amber-800 dark:text-amber-300">
            Total: <b>₹ {(form.local_misc_items || []).reduce((s, m) => s + num(m.amount), 0).toLocaleString("en-IN")}</b>
          </div>
        </div>
        <S5Misc
          items={form.local_misc_items || []}
          setItems={(v) => upd({ local_misc_items: v })}
          minDate={minDate}
          maxDate={maxDate}
          monthLabel={form.month && form.year ? `${form.month} ${form.year}` : ""}
        />
      </div>
    </div>
  );
}

/* =========================================================================
 * TOUR section
 * =======================================================================*/
function TourSection({ form, upd, clients, users, totals }) {
  const en = form.sections_enabled;
  const toggle = (k) => upd({ sections_enabled: { ...en, [k]: !en[k] } });

  return (
    <div className="space-y-4">
      {/* Header info */}
      <ClientMultiPicker clients={clients} valueIds={form.client_ids} onChange={(ids, names) => upd({ client_ids: ids, client_names: names })} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Purpose of Travel *</Label>
          <Textarea rows={2} value={form.purpose_of_travel} onChange={(e) => upd({ purpose_of_travel: e.target.value })} className="mt-1.5" data-testid="tour-purpose" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Assigned By *</Label>
          <UserSelect users={users} valueId={form.assigned_by_id} onChange={(id, name) => upd({ assigned_by_id: id, assigned_by_name: name })} testid="tour-assigned-by" />
        </div>
      </div>

      {/* Section 1 — Inter-state */}
      <TourSectionCard
        idx={1}
        title="Travel · Inter-State"
        subtitle="Different-city travel — Bus / Train / Flight lines"
        icon={<Plane size={14} />}
        enabled={en.s1}
        onToggle={() => toggle("s1")}
        total={totals.s1}
      >
        <S1Lines lines={form.s1_lines} setLines={(x) => upd({ s1_lines: x })} users={users} />
      </TourSectionCard>

      {/* Section 2 — Conveyance in the tour city */}
      <TourSectionCard
        idx={2}
        title="Conveyance Charges / Local Travel Within the City"
        subtitle="Auto / Bus / Other — day-wise entries"
        icon={<Bus size={14} />}
        enabled={en.s2}
        onToggle={() => toggle("s2")}
        total={totals.s2}
      >
        <S2Entries
          entries={form.s2_entries}
          setEntries={(x) => upd({ s2_entries: x })}
        />
      </TourSectionCard>

      {/* Section 3 — DA / Fooding */}
      <TourSectionCard
        idx={3}
        title="DA · Fooding Calculation"
        subtitle="Days × per-day allocation (Accounts may override)"
        icon={<Utensils size={14} />}
        enabled={en.s3}
        onToggle={() => toggle("s3")}
        total={totals.s3}
      >
        <S3DA s3={form.s3} setS3={(v) => upd({ s3: v })} />
      </TourSectionCard>

      {/* Section 4 — Lodging */}
      <TourSectionCard
        idx={4}
        title="Lodging"
        subtitle="Per-hotel breakdown"
        icon={<Building2 size={14} />}
        enabled={en.s4}
        onToggle={() => toggle("s4")}
        total={totals.s4}
      >
        <S4Hotels hotels={form.s4_hotels} setHotels={(v) => upd({ s4_hotels: v })} />
      </TourSectionCard>

      {/* Section 5 — Misc */}
      <TourSectionCard
        idx={5}
        title="Miscellaneous Expenses"
        subtitle="Any other expense with reason + proof"
        icon={<ClipboardList size={14} />}
        enabled={en.s5}
        onToggle={() => toggle("s5")}
        total={totals.s5}
      >
        <S5Misc items={form.s5_items} setItems={(v) => upd({ s5_items: v })} />
      </TourSectionCard>
    </div>
  );
}

function TourSectionCard({ idx, title, subtitle, icon, enabled, onToggle, total, children }) {
  return (
    <div className={`rounded-lg border ${enabled ? "border-slate-300" : "border-slate-200 opacity-70"} dark:border-slate-700 overflow-hidden`}>
      <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/60">
        <div>
          <div className="text-[13px] font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]">Section {idx}</Badge> {icon} {title}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-[12px] font-bold text-indigo-800 dark:text-indigo-300">₹ {total.toLocaleString("en-IN")}</div>
          <Switch checked={enabled} onCheckedChange={onToggle} data-testid={`s${idx}-toggle`} />
        </div>
      </div>
      {enabled && <div className="p-3">{children}</div>}
    </div>
  );
}

/* =========================================================================
 * Shared building blocks
 * =======================================================================*/
function ConveyanceRow({ c, modes, onChange, onRemove, canRemove }) {
  const showKms = c.mode === "Car" || c.mode === "Bike";
  const showAutoKms = c.mode === "Auto" && !c.has_bill;
  const computed = computeConveyanceAmount(c);
  const isAmountInput = !(showKms || showAutoKms);
  return (
    <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2 bg-white dark:bg-slate-900 space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {modes.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => onChange({ mode: m })}
            className={`text-[11px] px-2.5 py-1 rounded-full border ${
              c.mode === m ? "bg-indigo-600 text-white border-indigo-600" : "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600"
            }`}
          >
            {m}
          </button>
        ))}
        {canRemove && (
          <button type="button" onClick={onRemove} className="ml-auto text-slate-400 hover:text-rose-600 text-[11px] flex items-center gap-1">
            <X size={12} /> Remove
          </button>
        )}
      </div>
      {c.mode === "Other" && (
        <Input value={c.mode_other} onChange={(e) => onChange({ mode_other: e.target.value })} placeholder="Describe mode (e.g. Metro)" className="h-8 text-[12px]" />
      )}
      {(showKms || showAutoKms) && (
        <div className="flex items-center gap-2">
          <Input type="number" placeholder="KMs" value={c.kms} onChange={(e) => onChange({ kms: e.target.value })} className="h-8 text-[12px] max-w-[100px]" />
          <span className="text-[11px] text-slate-500">
            Auto: ₹ {computed.toLocaleString("en-IN")} @ ₹{RATE[c.mode]}/KM
          </span>
        </div>
      )}
      {isAmountInput && (
        <Input type="number" placeholder="Amount ₹" value={c.amount} onChange={(e) => onChange({ amount: e.target.value })} className="h-8 text-[12px]" />
      )}
      {c.mode && c.mode !== "Car" && c.mode !== "Bike" && (
        <div className="flex flex-wrap gap-2 items-center text-[11px]">
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={c.has_bill} onChange={(e) => onChange({ has_bill: e.target.checked })} data-testid="local-conv-has-proof" />
            <span>Do you have Invoice / Ticket / Payment Proof?</span>
          </label>
          {!c.has_bill && (
            <Input value={c.no_bill_reason} onChange={(e) => onChange({ no_bill_reason: e.target.value })} placeholder="Reason (required)" className="h-7 text-[11px] flex-1 min-w-[180px]" data-testid="local-conv-noproof-reason" />
          )}
        </div>
      )}
    </div>
  );
}

function ClientMultiPicker({ clients, valueIds, onChange, compact, label }) {
  const [q, setQ] = useState("");
  const selected = clients.filter((c) => valueIds.includes(c.id));
  const filtered = clients.filter((c) => !valueIds.includes(c.id) && (!q || c.client_name.toLowerCase().includes(q.toLowerCase())));
  const [open, setOpen] = useState(false);

  const add = (c) => {
    const ids = [...valueIds, c.id];
    const names = clients.filter((x) => ids.includes(x.id)).map((x) => x.client_name);
    onChange(ids, names);
    setQ(""); setOpen(false);
  };
  const remove = (id) => {
    const ids = valueIds.filter((x) => x !== id);
    const names = clients.filter((x) => ids.includes(x.id)).map((x) => x.client_name);
    onChange(ids, names);
  };

  return (
    <div>
      <Label className={`${compact ? "text-[10.5px]" : "text-[11px]"} uppercase tracking-wider font-semibold text-slate-600 flex items-center gap-1.5`}>
        <UsersIcon size={12} /> {label || "Clients / Trainings met (searchable — select multiple)"}
      </Label>
      <div className="mt-1.5 flex flex-wrap gap-1.5 items-center">
        {selected.map((s) => (
          <Badge key={s.id} className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[11px]">
            {s.client_name} <button onClick={() => remove(s.id)} className="ml-1 hover:text-rose-600"><X size={10} /></button>
          </Badge>
        ))}
        <div className="relative flex-1 min-w-[220px]">
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            placeholder="Type client / school…"
            className={compact ? "h-7 text-[11.5px]" : "h-8 text-[12px]"}
          />
          {open && filtered.length > 0 && (
            <div className="absolute z-50 mt-1 left-0 right-0 max-h-56 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg">
              {filtered.slice(0, 30).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); add(c); }}
                  className="w-full text-left px-3 py-1.5 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 text-[12px]"
                >
                  <div className="font-semibold">{c.client_name}</div>
                  <div className="text-[10.5px] text-slate-500">{c.district || ""} {c.state ? `· ${c.state}` : ""}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function UserSelect({ users, valueId, onChange, testid }) {
  return (
    <Select value={valueId || undefined} onValueChange={(v) => {
      const u = users.find((x) => x.id === v);
      onChange(v, u?.name || "");
    }}>
      <SelectTrigger className="mt-1.5" data-testid={testid}><SelectValue placeholder="Select an active user" /></SelectTrigger>
      <SelectContent className="max-h-80">
        {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-slate-500 ml-1">· {u.department || u.role}</span></SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger className="mt-1.5" data-testid={testid}><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent className="max-h-80">{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

/* ================= Tour section-1 (Inter-state lines) ================= */
function S1Lines({ lines, setLines, users }) {
  const setLine = (i, p) => setLines(lines.map((l, x) => (x === i ? { ...l, ...p } : l)));
  const add = () => setLines([...lines, blankS1Line()]);
  const rm  = (i) => setLines(lines.filter((_, x) => x !== i));
  return (
    <div className="space-y-3">
      {lines.map((l, i) => (
        <div key={i} className="rounded-md border border-slate-200 dark:border-slate-700 p-3 bg-slate-50/40 dark:bg-slate-900/40 space-y-2">
          <div className="flex justify-between items-center">
            <div className="text-[11.5px] font-semibold text-slate-600">Line #{i + 1}</div>
            {lines.length > 1 && <button onClick={() => rm(i)} className="text-rose-600 text-[11px] flex items-center gap-1"><Trash2 size={11} /> Remove</button>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <NumberedInput label="Departure Date *"     type="date"          value={l.departure_date}  onChange={(v) => setLine(i, { departure_date: v })} />
            <NumberedInput label="Departure Time *"     type="time"          value={l.departure_time}  onChange={(v) => setLine(i, { departure_time: v })} />
            <NumberedInput label="Departure Location *"                       value={l.departure_location} onChange={(v) => setLine(i, { departure_location: v })} />
            <NumberedInput label="Arrival Date *"       type="date"          value={l.arrival_date}    onChange={(v) => setLine(i, { arrival_date: v })} />
            <NumberedInput label="Arrival Time *"       type="time"          value={l.arrival_time}    onChange={(v) => setLine(i, { arrival_time: v })} />
            <NumberedInput label="Arrival Location *"                         value={l.arrival_location} onChange={(v) => setLine(i, { arrival_location: v })} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <SelectField label="Mode *" value={l.mode} onChange={(v) => setLine(i, { mode: v, travel_class: "" })} options={INTER_MODES} />
            <SelectField label="Class" value={l.travel_class} onChange={(v) => setLine(i, { travel_class: v })}
              options={l.mode === "Train" ? ["SL", "3AC", "2AC", "1AC", "Second Class", "Chair Economy", "Chair Premium"]
                     : l.mode === "Bus"   ? ["AC", "Non-AC"]
                     : l.mode === "Flight" ? ["Economy", "Business"] : []} />
            <NumberedInput label="Amount ₹ *" type="number" value={l.amount} onChange={(v) => setLine(i, { amount: v })} />
          </div>
          {l.mode === "Train" && (
            <label className="flex items-center gap-2 text-[11.5px]">
              <input type="checkbox" checked={l.tatkal} onChange={(e) => setLine(i, { tatkal: e.target.checked })} /> Tatkal Ticket
            </label>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
            <SelectField
              label="Booked By"
              value={l.booked_by}
              onChange={(v) => setLine(i, { booked_by: v, booked_by_user_id: "", booked_by_user_name: "" })}
              options={["self", "office", "user"]}
            />
            {l.booked_by === "user" && <UserSelect users={users} valueId={l.booked_by_user_id} onChange={(id, name) => setLine(i, { booked_by_user_id: id, booked_by_user_name: name })} />}
            <NumberedInput label="Remark" value={l.remark} onChange={(v) => setLine(i, { remark: v })} />
          </div>
          <div className="flex flex-wrap gap-2 items-center text-[11.5px]">
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={l.has_bill} onChange={(e) => setLine(i, { has_bill: e.target.checked })} />
              <span>Do you have Invoice / Ticket / Payment Proof?</span>
            </label>
            {!l.has_bill && <Input value={l.no_bill_reason} onChange={(e) => setLine(i, { no_bill_reason: e.target.value })} placeholder="Reason (required)" className="h-7 text-[11px] flex-1 min-w-[180px]" />}
          </div>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={add} className="h-8 text-[11.5px]"><Plus size={12} className="mr-1" /> Add another line</Button>
    </div>
  );
}

function S2Entries({ entries, setEntries }) {
  const setEntry = (i, p) => setEntries(entries.map((e, x) => (x === i ? { ...e, ...p } : e)));
  const setConv = (ei, ci, p) => setEntry(ei, {
    conveyances: entries[ei].conveyances.map((c, x) => (x === ci ? { ...c, ...p } : c)),
  });
  const add = () => setEntries([...entries, blankLocalEntry()]);
  const rm  = (i) => setEntries(entries.filter((_, x) => x !== i));
  return (
    <div className="space-y-3">
      {entries.map((e, ei) => (
        <div key={ei} className="rounded-md border border-slate-200 dark:border-slate-700 p-3 bg-slate-50/40 dark:bg-slate-900/40 space-y-2">
          <div className="flex justify-between items-center">
            <div className="text-[11.5px] font-semibold text-slate-600">Day #{ei + 1}</div>
            {entries.length > 1 && <button onClick={() => rm(ei)} className="text-rose-600 text-[11px] flex items-center gap-1"><Trash2 size={11} /> Remove</button>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <NumberedInput label="Date *"        type="date" value={e.date}           onChange={(v) => setEntry(ei, { date: v })} />
            <NumberedInput label="Start *"                    value={e.start_location} onChange={(v) => setEntry(ei, { start_location: v })} />
            <NumberedInput label="End *"                      value={e.end_location}   onChange={(v) => setEntry(ei, { end_location: v })} />
          </div>
          {e.conveyances.map((c, ci) => (
            <ConveyanceRow
              key={ci}
              c={c}
              modes={TOUR_LOCAL_MODES}
              onChange={(p) => setConv(ei, ci, p)}
              onRemove={() => setEntry(ei, { conveyances: e.conveyances.filter((_, x) => x !== ci) })}
              canRemove={e.conveyances.length > 1}
            />
          ))}
          <Button size="sm" variant="outline" onClick={() => setEntry(ei, { conveyances: [...e.conveyances, blankConv()] })} className="h-7 text-[11px]">
            <Plus size={11} className="mr-1" /> Add mode
          </Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={add} className="h-8 text-[11.5px]"><Plus size={12} className="mr-1" /> Add another Date</Button>
    </div>
  );
}

function S3DA({ s3, setS3 }) {
  const days = daysBetweenDT(s3.start_datetime, s3.end_datetime);
  const total = days * num(s3.per_day_amount);
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <NumberedInput label="Travel Start *" type="datetime-local" value={s3.start_datetime} onChange={(v) => setS3({ ...s3, start_datetime: v })} />
        <NumberedInput label="Travel End *"   type="datetime-local" value={s3.end_datetime}   onChange={(v) => setS3({ ...s3, end_datetime: v })} />
        <div>
          <Label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-600">Days</Label>
          <div className="mt-1.5 h-9 rounded-md border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 flex items-center text-[12.5px] font-semibold">{days || "—"}</div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
        <NumberedInput label="Per-Day Allocation (₹) *" type="number" value={s3.per_day_amount} onChange={(v) => setS3({ ...s3, per_day_amount: v })} />
        <div className="md:col-span-2 text-[12px] text-slate-600 dark:text-slate-300 rounded-md border border-dashed border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 p-2">
          Section 3 total: <b>₹ {total.toLocaleString("en-IN")}</b>. Accounts team may override this rate after submission.
        </div>
      </div>
    </div>
  );
}

function S4Hotels({ hotels, setHotels }) {
  const setH = (i, p) => setHotels(hotels.map((h, x) => (x === i ? { ...h, ...p } : h)));
  const add = () => setHotels([...hotels, blankHotel()]);
  const rm  = (i) => setHotels(hotels.filter((_, x) => x !== i));
  return (
    <div className="space-y-3">
      {hotels.map((h, i) => {
        const dayCt = daysBetween(h.from_date, h.to_date);
        const total = dayCt * num(h.amount_per_day);
        return (
          <div key={i} className="rounded-md border border-slate-200 dark:border-slate-700 p-3 bg-slate-50/40 dark:bg-slate-900/40 space-y-2">
            <div className="flex justify-between items-center">
              <div className="text-[11.5px] font-semibold text-slate-600">Hotel #{i + 1}</div>
              {hotels.length > 1 && <button onClick={() => rm(i)} className="text-rose-600 text-[11px] flex items-center gap-1"><Trash2 size={11} /> Remove</button>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <NumberedInput label="Date From *" type="date" value={h.from_date} onChange={(v) => setH(i, { from_date: v })} />
              <NumberedInput label="Date To *"   type="date" value={h.to_date}   onChange={(v) => setH(i, { to_date: v })} />
              <div>
                <Label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-600">Days</Label>
                <div className="mt-1.5 h-9 rounded-md border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 flex items-center text-[12.5px] font-semibold">{dayCt || "—"}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <NumberedInput label="Hotel Name *" value={h.hotel_name} onChange={(v) => setH(i, { hotel_name: v })} />
              <NumberedInput label="Location *"  value={h.location}   onChange={(v) => setH(i, { location: v })} />
              <NumberedInput label="Amount / Day *" type="number" value={h.amount_per_day} onChange={(v) => setH(i, { amount_per_day: v })} />
            </div>
            <div className="text-[11.5px] text-slate-600">Total: <b>₹ {total.toLocaleString("en-IN")}</b></div>
            <div className="flex flex-wrap gap-2 items-center text-[11.5px]">
              <label className="flex items-center gap-1">
                <input type="checkbox" checked={h.has_bill} onChange={(e) => setH(i, { has_bill: e.target.checked })} />
                <span>Do you have Invoice / Ticket / Payment Proof?</span>
              </label>
              {!h.has_bill && <Input value={h.no_bill_reason} onChange={(e) => setH(i, { no_bill_reason: e.target.value })} placeholder="Reason (required)" className="h-7 text-[11px] flex-1 min-w-[180px]" />}
            </div>
          </div>
        );
      })}
      <Button size="sm" variant="outline" onClick={add} className="h-8 text-[11.5px]"><Plus size={12} className="mr-1" /> Add another Hotel</Button>
    </div>
  );
}

function S5Misc({ items, setItems, minDate = "", maxDate = "", monthLabel = "" }) {
  const setIt = (i, p) => setItems(items.map((h, x) => (x === i ? { ...h, ...p } : h)));
  const add = () => setItems([...items, blankMisc()]);
  const rm  = (i) => setItems(items.filter((_, x) => x !== i));
  return (
    <div className="space-y-2">
      {items.map((m, i) => (
        <div key={i} className="rounded-md border border-slate-200 dark:border-slate-700 p-3 bg-slate-50/40 dark:bg-slate-900/40 space-y-2">
          <div className="flex justify-between items-center">
            <div className="text-[11.5px] font-semibold text-slate-600">Row #{i + 1}</div>
            {items.length > 1 && <button onClick={() => rm(i)} className="text-rose-600 text-[11px] flex items-center gap-1"><Trash2 size={11} /> Remove</button>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <Label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-600">Date *</Label>
              <Input
                type="date"
                value={m.date}
                min={minDate || undefined}
                max={maxDate || undefined}
                onChange={(e) => setIt(i, { date: e.target.value })}
                className="mt-1.5"
              />
              {minDate && monthLabel && (
                <div className="text-[9.5px] text-slate-500 mt-0.5">Must be within {monthLabel}</div>
              )}
            </div>
            <NumberedInput label="Expense Type *"                value={m.expense_type} onChange={(v) => setIt(i, { expense_type: v })} />
            <NumberedInput label="Amount ₹ *"    type="number"  value={m.amount}        onChange={(v) => setIt(i, { amount: v })} />
          </div>
          <NumberedInput label="Reason of Expense *" value={m.reason} onChange={(v) => setIt(i, { reason: v })} />
          <NumberedInput label="Remark" value={m.remark} onChange={(v) => setIt(i, { remark: v })} />
          <div className="flex flex-wrap gap-2 items-center text-[11.5px]">
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={m.has_bill} onChange={(e) => setIt(i, { has_bill: e.target.checked })} />
              <span>Do you have Invoice / Ticket / Payment Proof?</span>
            </label>
            {!m.has_bill && <Input value={m.no_bill_reason} onChange={(e) => setIt(i, { no_bill_reason: e.target.value })} placeholder="Reason (required)" className="h-7 text-[11px] flex-1 min-w-[180px]" />}
          </div>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={add} className="h-8 text-[11.5px]"><Plus size={12} className="mr-1" /> Add another Row</Button>
    </div>
  );
}

function NumberedInput({ label, type = "text", value, onChange }) {
  return (
    <div>
      <Label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1.5" />
    </div>
  );
}

/* =========================================================================
 * v109.9.106 — Instructions banner (shown right before printing the PDF).
 * Users click "Continue → Print" which triggers window.print() and marks
 * the settlement Downloaded on the backend.
 * =======================================================================*/
export function InstructionsBanner({ instructions, onCancel, onConfirm }) {
  const copy = (s) => {
    if (!s) return;
    try { navigator.clipboard?.writeText(s); toast.success("Copied"); } catch { /* ignore */ }
  };
  const toLine = (instructions.to || []).join(", ");
  const ccLine = (instructions.cc || []).join(", ");
  return (
    <Dialog open onOpenChange={(v) => !v && onCancel?.()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="ts-instructions">
        <DialogHeader>
          <DialogTitle className="heading">Before you download the settlement</DialogTitle>
          <p className="text-[12px] text-slate-500">
            Follow these steps carefully — the Accounts team relies on this consistent format for reconciliation.
          </p>
        </DialogHeader>
        <div className="space-y-3 py-2 text-[12.5px] text-slate-700 dark:text-slate-200">
          <div className="rounded-lg border-2 border-indigo-300 bg-indigo-50/60 dark:bg-indigo-950/30 p-3 flex items-center justify-between">
            <div>
              <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">Settlement Code</div>
              <div className="mono text-xl font-bold text-indigo-800 dark:text-indigo-300" data-testid="ts-inst-code">{instructions.settlement_code}</div>
            </div>
            <Button variant="outline" size="sm" onClick={() => copy(instructions.settlement_code)} className="h-7 text-[11px]">Copy</Button>
          </div>

          <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Send an e-mail</div>
            <Row label="To" value={toLine || "—"} onCopy={() => copy(toLine)} />
            <Row label="CC" value={ccLine || "—"} onCopy={() => copy(ccLine)} />
            <Row label="Subject" value={instructions.subject} onCopy={() => copy(instructions.subject)} />
          </div>

          <div className="rounded-lg border border-amber-300 bg-amber-50/60 dark:bg-amber-950/20 p-3">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-amber-800 dark:text-amber-200 mb-1">Attach the following (one file per line)</div>
            <ol className="pl-5 list-decimal space-y-0.5">
              <li>The downloaded Settlement PDF (this file) — attach first.</li>
              {(instructions.proof_lines || []).map((p, i) => (
                <li key={i} data-testid={`ts-inst-proof-${i}`}>{p.label}</li>
              ))}
              {(!instructions.proof_lines || instructions.proof_lines.length === 0) && (
                <li className="text-slate-500 italic">No proof attachments — every line was captured &quot;No proof + reason&quot;.</li>
              )}
            </ol>
          </div>

          <div className="text-[11.5px] text-slate-600 dark:text-slate-300 rounded-md bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 p-2.5">
            After you send the e-mail, come back to <b>My Settlements</b> and click
            <b> Submit for Approval</b>. You&apos;ll be asked to confirm the send + provide
            your sender e-mail id.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="ts-inst-cancel">Cancel</Button>
          <Button onClick={onConfirm} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="ts-inst-confirm">
            <Printer size={13} className="mr-1.5" /> Continue → Print PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, onCopy }) {
  return (
    <div className="flex items-start gap-2">
      <div className="w-16 shrink-0 text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 pt-0.5">{label}</div>
      <div className="flex-1 text-slate-800 dark:text-slate-100 break-all">{value}</div>
      <button onClick={onCopy} className="text-[11px] text-indigo-700 hover:underline shrink-0">Copy</button>
    </div>
  );
}
